// src/pages/Conversations.jsx
import { normalizeMediaUrl } from "../lib/media";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api"; // ✅ centralized axios instance
import { socket, connectSocketOnce } from "../socket";

import BottomNav from "../components/BottomNav";

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [menuOpenId, setMenuOpenId] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
  const closeMenu = () => setMenuOpenId(null);

  window.addEventListener("click", closeMenu);

  return () => {
    window.removeEventListener("click", closeMenu);
  };
}, []);

useEffect(() => {
  const token = localStorage.getItem("token");
  if (!token) return; // ✅ prevent invalid token error

  connectSocketOnce();

  const updateConversation = (msg) => {
    setConversations((prev) => {
      let updated = [...prev];

      const index = updated.findIndex(
        (c) => String(c.conversation_id) === String(msg.conversation_id)
      );

      if (index !== -1) {
        const conv = updated[index];

        const updatedConv = {
          ...conv,
          last_message: msg.text,
          last_message_at: msg.created_at,
        };

        updated.splice(index, 1);
        return [updatedConv, ...updated];
      }

      return [
        {
          conversation_id: msg.conversation_id,
          last_message: msg.text,
          last_message_at: msg.created_at,
          username: "New chat", // ✅ fallback
        },
        ...updated,
      ];
    });
  };

  const onMessage = (data) => updateConversation(data.message);
  const onLocalMessage = (msg) => updateConversation(msg);

  socket.on("message", onMessage);
  socket.on("message_sent_local", onLocalMessage);

  return () => {
    socket.off("message", onMessage);
    socket.off("message_sent_local", onLocalMessage);
  };
}, []);

  useEffect(() => {
    loadConversations();
  }, []);
  useEffect(() => {
  if (!socket) return;

  const handleOnline = ({ userId }) => {
    setOnlineUsers(prev => new Set(prev).add(String(userId)));
  };

  const handleOffline = ({ userId }) => {
    setOnlineUsers(prev => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  socket.on("user_online", handleOnline);
  socket.on("user_offline", handleOffline);

  return () => {
    socket.off("user_online", handleOnline);
    socket.off("user_offline", handleOffline);
  };
}, []);

  const loadConversations = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data?.conversations || []);
    } catch (err) {
      console.error("Failed to load conversations", err?.response?.data || err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId) => {
  if (!window.confirm("Delete this conversation?")) return;

  try {
    await api.delete(`/conversations/${conversationId}`);

    setConversations(prev =>
      prev.filter(c => c.conversation_id !== conversationId)
    );

  } catch (e) {
    alert("Failed to delete conversation");
  }
};

const openChat = (conversationId, otherUser) => {
  navigate(`/chat/${conversationId}`, {
    state: { user: otherUser },
  });
};

  if (loading) {
    return <div style={{ padding: 20 }}>Loading conversations…</div>;
  }

  return (
  <>
    <div   className="page has-bottom-nav"   style={{     maxWidth: 720,     paddingTop: "calc(60px + env(safe-area-inset-top))"   }} >
      {/* Header */}
      <div
        style={{
          padding: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          position: "sticky",
          top: "env(safe-area-inset-top)",
          background: "#020617",
          zIndex: 5
        }}
      >
        <h2 style={{ margin: 0, color: "#fff" }}>Messages</h2>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/conversations/new")}
        >
          + New
        </button>
      </div>

      {/* Empty state */}
      {conversations.length === 0 && (
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <p>No conversations yet</p>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/conversations/new")}
          >
            Start New Conversation
          </button>
        </div>
      )}

      {/* Conversation list */}
      {conversations.map((c) => (
        <div
          key={c.conversation_id}
          onClick={() =>
            openChat(c.conversation_id, {
              id: c.id,
              username: c.username,
              user_id: c.user_id,
              profile_pic_url: c.profile_pic_url,
            })
          }
          style={{
            display: "flex",
            gap: 12,
            padding: 14,
            borderBottom: "1px solid rgba(255,255,255,0.15)",
            cursor: "pointer",
            alignItems: "center",
            background: "rgba(255,255,255,0.03)"
          }}
        >
          {/* Avatar */}
          <div style={{ position: "relative" }}>
            <img
              src={
                normalizeMediaUrl(c.profile_pic_url) ||
                "/uploads/default-avatar.png"
              }
              alt=""
              style={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />

            {onlineUsers.has(String(c.id)) && (
              <span
                style={{
                  position: "absolute",
                  bottom: 2,
                  right: 2,
                  width: 10,
                  height: 10,
                  backgroundColor: "#22c55e",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </div>

          {/* Text */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                color: "#ffffff",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {c.username || c.user_id}
            </div>

            <div
              style={{
                color: "#9ca3af",
                fontSize: 14,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {c.last_message || "No messages yet"}
            </div>
          </div>

          {/* Right side */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8
            }}
          >
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)" }}>
              {c.last_message_at
                ? new Date(c.last_message_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : ""}
            </div>

            <div style={{ position: "relative" }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpenId(
                    menuOpenId === c.conversation_id ? null : c.conversation_id
                  );
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 18,
                  color: "#ffffff"
                }}
              >
                ⋮
              </button>

              {menuOpenId === c.conversation_id && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "100%",
                    marginTop: 6,
                    background: "#111827",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    padding: 6,
                    width: 120,
                    boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                    zIndex: 1000
                  }}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(c.conversation_id);
                    }}
                    onMouseEnter={(e) =>
                      (e.target.style.background = "rgba(255,255,255,0.08)")
                    }
                    onMouseLeave={(e) =>
                      (e.target.style.background = "transparent")
                    }
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#ef4444",
                      cursor: "pointer",
                      width: "100%",
                      textAlign: "left",
                      padding: 6,
                      borderRadius: 4
                    }}
                  >
                    Delete chat
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>

    <BottomNav />
  </>
);
}